import React, { useEffect, useState } from 'react';

import CombinedClientScopeFilters from './CombinedClientScopeFilters';
import useCombinedReportScope from './useCombinedReportScope';
import { fetchCombinedCapitalGainReport, resolveCombinedReportError } from './combinedReportUtils';
import '../capital-gain/CapitalGain.css';
import '../consolidated-reports/ConsolidatedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
};

const CombinedCapitalGains = () => {
  const {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds,
    effectiveClientIds,
    scopeSummary,
    selectionPreview,
    onScopeModeChange,
    onToggleClientId,
  } = useCombinedReportScope();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!financialYear || effectiveClientIds.length === 0) {
      setData(null);
      setError('');
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    fetchCombinedCapitalGainReport(apiUrl, effectiveClientIds, financialYear, {
      includePeriods: true,
      allAccessibleClients: clientScopeMode === 'all',
    })
      .then((response) => {
        if (ignore) {
          return;
        }
        setData(response);
        setError('');
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(resolveCombinedReportError(requestError, 'Unable to load the combined capital gains report.'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [clientScopeMode, effectiveClientIds, financialYear]);

  return (
    <div className="capital-gain-page combined-report-page">
      <div className="combined-report-header">
        <p className="consolidated-reports-eyebrow">Combined Reports</p>
        <h2 className="capital-gain-title">Combined Capital Gains</h2>
        <p className="financial-page-subtitle">Inspect LTCG, STCG, and duration buckets across all accessible clients or a selected client subset.</p>
      </div>

      <CombinedClientScopeFilters
        clientOptions={clientOptions}
        clientsLoading={clientsLoading}
        clientScopeMode={clientScopeMode}
        selectedClientIds={selectedClientIds}
        effectiveClientIds={effectiveClientIds}
        financialYear={financialYear}
        onScopeModeChange={onScopeModeChange}
        onToggleClientId={onToggleClientId}
      />

      {!financialYear ? <p className="financial-message">Select a financial year from the header to load the combined report.</p> : null}
      {financialYear && clientOptions.length > 0 && effectiveClientIds.length === 0 ? <p className="financial-message">Choose one or more client IDs to load the combined report.</p> : null}
      {loading && <p className="financial-message">Loading combined capital gains...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      {data ? (
        <>
          <section className="consolidated-reports-stat-grid">
            <article className="consolidated-reports-stat-card">
              <span>Scope</span>
              <strong>{scopeSummary}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Gross LTCG</span>
              <strong>₹ {formatAmount(data.ltcgTotal)}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Gross STCG</span>
              <strong>₹ {formatAmount(data.stcgTotal)}</strong>
            </article>
            <article className="consolidated-reports-stat-card consolidated-reports-stat-card--accent">
              <span>Taxable Capital Gains</span>
              <strong>₹ {formatAmount(Number(data.stcgTaxableTotal ?? 0) + Number(data.ltcgTaxableTotal ?? 0))}</strong>
            </article>
          </section>

          <p className="combined-report-caption">Included client IDs: {selectionPreview}</p>

          <div className="capital-gain-section">
            <h3>Long-Term Capital Gain (LTCG)</h3>
            {data.ltcg && data.ltcg.length > 0 ? (
              <table className="capital-gain-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Asset</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Acquired</th>
                    <th>Transferred</th>
                    <th>Holding Days</th>
                    <th>Gross Consideration</th>
                    <th>Transfer Expenses</th>
                    <th>Actual Cost</th>
                    <th>31-Jan-2018 FMV</th>
                    <th>Deemed Cost</th>
                    <th>Cost of Improvement</th>
                    <th>Gain</th>
                    <th>Rate %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ltcg.map((gain) => (
                    <tr key={gain.combinedRowId}>
                      <td>{gain.clientId}</td>
                      <td>{gain.assetName}</td>
                      <td>{gain.assetClass}</td>
                      <td>{gain.section}</td>
                      <td>{formatDate(gain.acquisitionDate)}</td>
                      <td>{formatDate(gain.transferDate)}</td>
                      <td>{gain.holdingPeriodDays}</td>
                      <td>₹ {formatAmount(gain.grossConsideration)}</td>
                      <td>₹ {formatAmount(gain.expenseRelatedToTransferExceptSTT)}</td>
                      <td>₹ {formatAmount(gain.actualCostOfAcquisition)}</td>
                      <td>{gain.grandfatheredFairMarketValue != null ? `₹ ${formatAmount(gain.grandfatheredFairMarketValue)}` : '-'}</td>
                      <td>₹ {formatAmount(gain.costOfAcqusition)}</td>
                      <td>{gain.costOfImprovement != null ? `₹ ${formatAmount(gain.costOfImprovement)}` : 'N/A'}</td>
                      <td>₹ {formatAmount(gain.gainAmount)}</td>
                      <td>{formatAmount(gain.applicableTaxRate)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="13">Gross LTCG</td>
                    <td>₹ {formatAmount(data.ltcgTotal)}</td>
                    <td>-</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan="13">112A Threshold Relief</td>
                    <td>₹ {formatAmount(data.ltcgThresholdRelief)}</td>
                    <td>-</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan="13">Taxable LTCG</td>
                    <td>₹ {formatAmount(data.ltcgTaxableTotal)}</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p>No Long-Term Capital Gain data available.</p>
            )}
          </div>

          <div className="capital-gain-section">
            <h3>Short-Term Capital Gain (STCG)</h3>
            {data.stcg && data.stcg.length > 0 ? (
              <table className="capital-gain-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Asset</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Acquired</th>
                    <th>Transferred</th>
                    <th>Holding Days</th>
                    <th>Gross Consideration</th>
                    <th>Transfer Expenses</th>
                    <th>Actual Cost</th>
                    <th>31-Jan-2018 FMV</th>
                    <th>Deemed Cost</th>
                    <th>Cost of Improvement</th>
                    <th>Gain</th>
                    <th>Rate %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stcg.map((gain) => (
                    <tr key={gain.combinedRowId}>
                      <td>{gain.clientId}</td>
                      <td>{gain.assetName}</td>
                      <td>{gain.assetClass}</td>
                      <td>{gain.section}</td>
                      <td>{formatDate(gain.acquisitionDate)}</td>
                      <td>{formatDate(gain.transferDate)}</td>
                      <td>{gain.holdingPeriodDays}</td>
                      <td>₹ {formatAmount(gain.grossConsideration)}</td>
                      <td>₹ {formatAmount(gain.expenseRelatedToTransferExceptSTT)}</td>
                      <td>₹ {formatAmount(gain.actualCostOfAcquisition)}</td>
                      <td>{gain.grandfatheredFairMarketValue != null ? `₹ ${formatAmount(gain.grandfatheredFairMarketValue)}` : '-'}</td>
                      <td>₹ {formatAmount(gain.costOfAcqusition)}</td>
                      <td>{gain.costOfImprovement != null ? `₹ ${formatAmount(gain.costOfImprovement)}` : 'N/A'}</td>
                      <td>₹ {formatAmount(gain.gainAmount)}</td>
                      <td>{formatAmount(gain.applicableTaxRate)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan="13">Gross STCG</td>
                    <td>₹ {formatAmount(data.stcgTotal)}</td>
                    <td>-</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan="13">Taxable STCG</td>
                    <td>₹ {formatAmount(data.stcgTaxableTotal)}</td>
                    <td>-</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p>No Short-Term Capital Gain data available.</p>
            )}
          </div>

          <div className="capital-gain-section">
            <h3>Duration of Short-Term Capital Gains (111A)</h3>
            <table className="duration-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.stcgPeriod.map((period) => (
                  <tr key={period.name}>
                    <td>{period.name}</td>
                    <td>₹ {formatAmount(period.amount)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Short-Term Capital Gain</td>
                  <td>₹ {formatAmount(data.stcgTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="capital-gain-section">
            <h3>Duration of Long-Term Capital Gains (112A)</h3>
            <table className="duration-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.ltcgPeriod.map((period) => (
                  <tr key={period.name}>
                    <td>{period.name}</td>
                    <td>₹ {formatAmount(period.amount)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Long-Term Capital Gain</td>
                  <td>₹ {formatAmount(data.ltcgTotal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default CombinedCapitalGains;